import { useEffect } from 'react';
import { logCspViolationDetailsToConsole } from '../api/host-by-env';

const logToConsole = logCspViolationDetailsToConsole();

/**
 * CSP Violation Console Logger
 *
 * This hook listens for Content Security Policy violations and logs them to the console
 * to help identify missing CSP directives or blocked resources.
 *
 * Reporting Architecture (ANS-9464):
 * - Browser-native reporting: Browser automatically sends violations to backend via 'report-to' directive
 *   (configured in craco.config.js) - works even if JavaScript fails
 * - Console logging (this hook): Provides visibility in browser DevTools for all environments
 *
 * Environment-specific behavior:
 * - All environments (including PROD): Logs detailed violation info to console
 * - Can be disabled per environment via logCspViolationDetailsToConsole flag in host-by-env.ts
 *
 * Usage in App.tsx:
 *   import { useCspViolationReporter } from './hooks/useCspViolationReporter';
 *
 *   function App() {
 *     useCspViolationReporter();
 *     return <YourApp />;
 *   }
 */

interface CspViolationEvent extends SecurityPolicyViolationEvent {
  blockedURI: string;
  violatedDirective: string;
  effectiveDirective: string;
  originalPolicy: string;
  sourceFile: string;
  lineNumber: number;
  columnNumber: number;
}

interface CspViolationReporterOptions {
  /**
   * Current environment name (e.g., 'PROD', 'QA1', 'DEV1')
   * Used to determine logging behavior:
   * - PROD: Only sends to API (no console logging)
   * - All other environments: Logs to console AND sends to API
   */
  environment: string;

  /**
   * Enable/disable violation reporting
   * @default true
   */
  enabled?: boolean;

  /**
   * Send violations to a remote endpoint
   * @default undefined (no remote reporting)
   */
  reportEndpoint?: string;

  /**
   * Custom handler for violations
   */
  onViolation?: (violation: CspViolationReport) => void;
}

export interface CspViolationReport {
  timestamp: string;
  blockedURI: string;
  violatedDirective: string;
  effectiveDirective: string;
  sourceFile: string;
  lineNumber: number;
  columnNumber: number;
  originalPolicy: string;
  userAgent: string;
  documentURI: string;
}

/**
 * Format CSP violation for logging
 */
function formatViolation(violation: CspViolationReport): string {
  return `
🚨 CSP Violation Detected
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Blocked Resource:    ${violation.blockedURI}
Violated Directive:  ${violation.violatedDirective}
Effective Directive: ${violation.effectiveDirective}
Source File:         ${violation.sourceFile}:${violation.lineNumber}:${violation.columnNumber}
Timestamp:           ${violation.timestamp}
Document URI:        ${violation.documentURI}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💡 Fix: Add the blocked domain to the '${violation.effectiveDirective}' directive in craco.config.js
`;
}

/**
 * React hook to log CSP violations to console for visibility
 * Browser natively reports violations to backend via 'report-to' directive
 */
export function useCspViolationReporter() {
  useEffect(() => {
    // Skip if console logging is disabled for this environment
    if (!logToConsole) {
      return;
    }

    // Handle CSP violation events
    const handleViolation = (event: Event) => {
      const violation = event as unknown as CspViolationEvent;

      const report: CspViolationReport = {
        timestamp: new Date().toISOString(),
        blockedURI: violation.blockedURI || 'inline',
        violatedDirective: violation.violatedDirective,
        effectiveDirective: violation.effectiveDirective,
        sourceFile: violation.sourceFile || 'unknown',
        lineNumber: violation.lineNumber || 0,
        columnNumber: violation.columnNumber || 0,
        originalPolicy: violation.originalPolicy,
        userAgent: navigator.userAgent,
        documentURI: violation.documentURI || document.location.href,
      };

      // Log to console for developer visibility
      console.error(formatViolation(report));
      console.groupCollapsed('Full violation details');
      console.table(report);
      console.groupEnd();
    };

    // Listen for CSP violation events
    document.addEventListener('securitypolicyviolation', handleViolation);

    return () => {
      document.removeEventListener('securitypolicyviolation', handleViolation);
    };
  }, []);
}

/**
 * Get common CSP violations and their fixes
 */
export function getCspViolationFixes(): Record<string, string> {
  return {
    'script-src': "Add the script domain to 'script-src' directive in craco.config.js",
    'style-src': "Add the stylesheet domain to 'style-src' directive in craco.config.js",
    'img-src': "Add the image domain to 'img-src' directive in craco.config.js",
    'connect-src': "Add the API domain to 'connect-src' directive in craco.config.js",
    'frame-src': "Add the iframe domain to 'frame-src' directive in craco.config.js",
    'font-src': "Add the font domain to 'font-src' directive in craco.config.js",
    'media-src': "Add the media domain to 'media-src' directive in craco.config.js",
  };
}
